import { OverlayTrigger, Popover } from 'react-bootstrap';
import { KildeDetaljerViewModel } from 'services/KjederegisteretAdminApi/EnhetKildeApi/types';
import {
  KILDE_LABELS,
  buildKildeLink,
} from 'services/KjederegisteretAdminApi/EnhetKildeApi/constants';

// Each source names its fields differently (Google has no "epost"/"oppstartsdato" concept,
// Brreg has no "nettside") - this maps our canonical field name to each source's own key.
const FIELD_KEY_BY_SOURCE: Record<string, Record<string, string>> = {
  'brreg-underenhet': { oppstartsdato: 'oppstartsdato', epost: 'epost', telefon: 'telefon' },
  'google-places': { telefon: 'telefon', hjemmeside: 'nettside' },
};

const defaultNormalize = (v: string) => (v || '').trim().toLowerCase();

interface SourceValue {
  kilde: string;
  eksternId: string;
  value: string;
}

interface Props {
  field: string;
  kildeDetaljer: KildeDetaljerViewModel[];
  currentValue?: string;
  onUseValue: (value: string) => void;
  formatValue?: (raw: string) => string;
  normalize?: (raw: string) => string;
}

const FieldSourcesPopover = (props: Props) => {
  const values: SourceValue[] = props.kildeDetaljer
    .map((k) => {
      const fieldKey = FIELD_KEY_BY_SOURCE[k.kilde]?.[props.field];
      const raw = fieldKey ? k.fields?.[fieldKey] : undefined;
      return raw ? { kilde: k.kilde, eksternId: k.eksternId, value: raw } : undefined;
    })
    .filter((x): x is SourceValue => !!x);

  if (values.length === 0) return null;

  const normalize = props.normalize ?? defaultNormalize;
  const matchesCurrent = (value: string) => normalize(value) === normalize(props.currentValue);
  const allMatch = values.every((v) => matchesCurrent(v.value));
  const badgeVariant = allMatch ? 'success' : 'warning';

  return (
    <OverlayTrigger
      trigger="click"
      placement="right"
      rootClose
      overlay={
        <Popover>
          <Popover.Header>Verdier fra kilder</Popover.Header>
          <Popover.Body>
            {values.map((v) => {
              const matches = matchesCurrent(v.value);
              const sourceLink = buildKildeLink(v.kilde, v.eksternId);
              return (
                <div key={v.kilde} className="mb-1">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      props.onUseValue(v.value);
                    }}
                  >
                    {props.formatValue ? props.formatValue(v.value) : v.value}
                  </a>
                  <br />
                  <small className={matches ? 'text-success' : 'text-warning'}>
                    {KILDE_LABELS[v.kilde] ?? v.kilde} {matches ? '(samsvarer)' : '(avviker)'}
                  </small>
                  {sourceLink && (
                    <>
                      {' '}
                      <a
                        href={sourceLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted"
                        title="Åpne kilden"
                      >
                        <small>↗</small>
                      </a>
                    </>
                  )}
                </div>
              );
            })}
          </Popover.Body>
        </Popover>
      }
    >
      <span
        role="button"
        className={`badge rounded-pill bg-${badgeVariant} ms-1`}
        style={{ cursor: 'pointer', fontSize: '0.7em', verticalAlign: 'middle' }}
      >
        {values.length} {values.length === 1 ? 'kilde' : 'kilder'}
      </span>
    </OverlayTrigger>
  );
};

export default FieldSourcesPopover;
